import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, ShieldAlert } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard, LoadingState } from "../../../components/ui";
import { expensePolicyService } from "../../../services/expensePolicyService";
import { conveyanceClaimService } from "../../../services/conveyanceClaimService";
import { availableCategories, enabledModes } from "../lib/claimRules";

/**
 * Getting what the ESS claim screens need before they can render, and what
 * they show while they cannot have it.
 *
 * These live together because every claim screen calls the hook, then renders
 * one of the two gate screens below until it resolves.
 */

/**
 * A Radix Select that unmounts while its dropdown is open leaves
 * `pointer-events: none` on <body>, which freezes scrolling and clicking until
 * the next full render. The claim form does exactly that — choosing a Reason
 * swaps the Customer select for a Description input — so the stale lock is
 * cleared whenever no Radix popper is left on the page.
 */
function useReleaseStalePointerLock() {
  useEffect(() => {
    let frame = 0;

    // Checked a frame late on purpose: Radix sets the lock before its popper
    // mounts, so an immediate check would clear a lock that is about to become
    // legitimate.
    const release = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (document.body.style.pointerEvents !== "none") return;
        if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
        document.body.style.pointerEvents = "";
      });
    };

    const observer = new MutationObserver(release);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
      childList: true,
    });

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      document.body.style.pointerEvents = "";
    };
  }, []);
}

/**
 * The active conveyance policy, its mode rows and the tenant's currency
 * metrics — everything a claim screen reads before rendering a single field.
 *
 * Which categories the New Claim control offers, which modes the dropdown
 * lists, whether tolls/parking columns exist and what the maintenance caps are
 * are ALL policy reads, so a screen that rendered first would have to guess.
 *
 * Currencies come from the metrics master and feed the selector beside every
 * amount field; the selected row's id is what a claim stores in metric_id.
 *
 * A missing policy is not an error — it means HR has not configured Local
 * Conveyance yet, and the screens say so instead of offering a broken form.
 */
export function useConveyancePolicy() {
  const [policy, setPolicy] = useState(null);
  const [modes, setModes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useReleaseStalePointerLock();

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    (async () => {
      try {
        const loadedPolicy = await expensePolicyService.fetchPolicy();
        const [loadedModes, loadedCurrencies] = await Promise.all([
          loadedPolicy?.policyId
            ? expensePolicyService.fetchModes(loadedPolicy.policyId)
            : Promise.resolve([]),
          // Non-fatal: metric_id is nullable, so a bare master must not block
          // an employee from filing a claim.
          conveyanceClaimService.fetchCurrencies().catch((currencyError) => {
            console.warn("Currency metric lookup failed:", currencyError);
            return [];
          }),
        ]);

        if (cancelled) return;
        setPolicy(loadedPolicy);
        setModes(loadedModes);
        setCurrencies(loadedCurrencies);
      } catch (loadError) {
        if (cancelled) return;
        console.error("Failed to load conveyance policy:", loadError);
        setError(loadError.message || "");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  const categories = useMemo(() => availableCategories(policy), [policy]);
  const modeOptions = useMemo(() => enabledModes(modes), [modes]);

  return {
    loading,
    error,
    policy,
    modes,
    modeOptions,
    categories,
    currencies,
    /** HR has not saved a policy row yet — no claim can be raised. */
    isConfigured: Boolean(policy?.policyId),
    reload: load,
  };
}

/**
 * Just the tenant's currency metrics, for screens that render claim amounts
 * but have no policy to read — the approver views. Every claim snapshots the
 * metric id it was submitted under, so an amount is only meaningful next to
 * the abbreviation that metric resolves to.
 *
 * Non-fatal on failure, for the same reason as above: metric_id is nullable,
 * and a bare master must not stop an approver from reading a queue. Amounts
 * then render as bare numbers rather than carrying a currency nobody
 * confirmed.
 */
export function useClaimCurrencies() {
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    let cancelled = false;
    conveyanceClaimService
      .fetchCurrencies()
      .then((rows) => {
        if (!cancelled) setCurrencies(rows);
      })
      .catch((error) => console.warn("Currency metric lookup failed:", error));
    return () => {
      cancelled = true;
    };
  }, []);

  return currencies;
}

/* ------------------------------------------------------------ gate screens */

export function ClaimLoadingScreen({ message }) {
  const { t } = useTranslation();
  return (
    <AppLayout>
      <ContentCard>
        <LoadingState message={message || t("conveyanceClaim.loadingPolicy")} />
      </ContentCard>
    </AppLayout>
  );
}

export function ClaimBlockedScreen({ title, message, onRetry }) {
  const { t } = useTranslation();
  return (
    <AppLayout>
      <ContentCard bodyClassName="p-10 text-center">
        <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-workspace/60 grid place-items-center per-file-hint">
          <ShieldAlert className="h-6 w-6" />
        </div>
        <p className="text-sm font-bold text-foreground">{title}</p>
        <p className="mt-1 text-sm per-file-hint max-w-[460px] mx-auto">
          {message}
        </p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-(--color-text-border) text-sm font-semibold text-foreground hover:bg-gray-50 transition cursor-pointer"
          >
            <RefreshCw className="h-4 w-4" />
            {t("localConveyance.retry")}
          </button>
        ) : null}
      </ContentCard>
    </AppLayout>
  );
}
